import { getLabelValue, getTeamName, LinkCreationService, setNationalParticipantBorder } from '@sportal365/smp-widget-utils';
import { NOT_AVAILABLE } from '../../../../packages/constants/notAvailable';
import {
  IFootballConfig,
  IForm,
  IHeadLabel,
  INormalizedStandingColumn,
  INormalizedTeamForm,
  IStanding,
  IStandingsConfig,
} from '../../../../packages/types';
import { TARGET_BLANK } from '../../../../packages/constants/windowTarget';
import { TEAM_ENTITY } from '../../../../packages/constants/entityTypes';
import { normalizeTeamFormData } from '@sportal365/smp-widget-normalizers';
import { assetsPlaceholder } from '../../../../packages/constants/assetsPlaceholder';
import {
  footballStandingsDefaultDataElements,
  footballStandingsMobileDefaultDataElements,
} from '../../../../packages/constants/dataElements';
import { FootballStandingsElements } from '../../../../packages/constants/sportType';
import { CSS } from '../../../../packages/theme/stitches.config';
import { NATIONAL } from '../../../../packages/constants/sportRegions';

export const checkOrGetDesktopDefaultDataElements = (dataElements: string[] | undefined) => {
  return !dataElements || dataElements.length === 0 ? footballStandingsDefaultDataElements : dataElements;
};

export const checkOrGetMobileDefaultDataElements = (dataElements: string[] | undefined) => {
  return !dataElements || dataElements.length === 0 ? footballStandingsMobileDefaultDataElements : dataElements;
};

// Standings columns header related
export const generateFootballStandingsColumnsLabels = (dataLabels: IStandingsConfig['dataLabels']) => {
  // labels
  const labelTeam = getLabelValue(dataLabels?.team, 'TEAM');
  const labelMatches = getLabelValue(dataLabels?.matches_played_short, 'M');
  const labelWins = getLabelValue(dataLabels?.matches_won_short, 'W');
  const labelLosses = getLabelValue(dataLabels?.match_losses_short, 'L');
  const labelPoints = getLabelValue(dataLabels?.points_short, 'P');
  const labelGoalDiff = getLabelValue(dataLabels?.goal_difference_short, 'GD');
  const labelDraws = getLabelValue(dataLabels?.match_draws_short, 'D');
  const labelTeamForm = getLabelValue(dataLabels?.team_form_short, 'TEAM FORM');

  // tooltips
  const tooltipMatchesPlayed = getLabelValue(dataLabels?.matches_played, 'MATCHES PLAYED');
  const tooltipMatchesWon = getLabelValue(dataLabels?.matches_won, 'MATCHES WON');
  const tooltipMatchDraws = getLabelValue(dataLabels?.match_draw, 'MATCH DRAWS');
  const tooltipMatchLosses = getLabelValue(dataLabels?.match_losses, 'MATCH LOSSES');
  const tooltipGoalDiff = getLabelValue(dataLabels?.goal_difference, 'GOAL DIFFERENCE');
  const tooltipPoints = getLabelValue(dataLabels?.points, 'POINTS');
  const tooltipTeamForm = getLabelValue(dataLabels?.team_form, 'TEAM FORM');

  return {
    labels: {
      labelTeam,
      labelMatches,
      labelWins,
      labelLosses,
      labelPoints,
      labelGoalDiff,
      labelDraws,
      labelTeamForm,
    },
    tooltips: {
      tooltipMatchesPlayed,
      tooltipMatchesWon,
      tooltipMatchDraws,
      tooltipMatchLosses,
      tooltipGoalDiff,
      tooltipPoints,
      tooltipTeamForm,
    },
  };
};

export const getLabelByDataElement = (dataElement: string, labels: Record<string, string>) => {
  const { labelMatches, labelWins, labelLosses, labelPoints, labelGoalDiff, labelDraws, labelTeamForm } = labels;

  switch (dataElement) {
    case FootballStandingsElements.MATCHES_PLAYED:
      return labelMatches;
    case FootballStandingsElements.MATCHES_WON:
      return labelWins;
    case FootballStandingsElements.MATCH_DRAW:
      return labelDraws;
    case FootballStandingsElements.MATCH_LOSSES:
      return labelLosses;
    case FootballStandingsElements.GOAL_DIFFERENCE:
      return labelGoalDiff;
    case FootballStandingsElements.POINTS:
      return labelPoints;
    case FootballStandingsElements.TEAM_FORM:
      return labelTeamForm;
    default:
      return NOT_AVAILABLE;
  }
};

export const getTooltipByDataElement = (dataElement: string, tooltips: Record<string, string> | undefined) => {
  if (tooltips) {
    const {
      tooltipMatchesPlayed,
      tooltipMatchesWon,
      tooltipMatchDraws,
      tooltipMatchLosses,
      tooltipGoalDiff,
      tooltipPoints,
      tooltipTeamForm,
    } = tooltips;

    switch (dataElement) {
      case FootballStandingsElements.MATCHES_PLAYED:
        return tooltipMatchesPlayed;
      case FootballStandingsElements.MATCHES_WON:
        return tooltipMatchesWon;
      case FootballStandingsElements.MATCH_DRAW:
        return tooltipMatchDraws;
      case FootballStandingsElements.MATCH_LOSSES:
        return tooltipMatchLosses;
      case FootballStandingsElements.GOAL_DIFFERENCE:
        return tooltipGoalDiff;
      case FootballStandingsElements.POINTS:
        return tooltipPoints;
      case FootballStandingsElements.TEAM_FORM:
        return tooltipTeamForm;
      default:
        return NOT_AVAILABLE;
    }
  }

  return undefined;
};

export const createColumnHeader =
  (isMobile: boolean) =>
  (dataElement: string, label: string, tooltip?: string): IHeadLabel => {
    switch (dataElement) {
      case FootballStandingsElements.GOAL_DIFFERENCE:
        return {
          value: label,
          tooltip,
          css: { width: isMobile ? undefined : '$8', textAlign: 'center' },
        };
      case FootballStandingsElements.POINTS:
        return {
          value: label,
          tooltip,
          css: { width: isMobile ? undefined : '$6', textAlign: 'center' },
        };
      case FootballStandingsElements.TEAM_FORM:
        return {
          value: label,
          tooltip,
          css: { width: '101px', textAlign: 'center', ml: '6px' },
        };
      default:
        return {
          value: label,
          tooltip,
          css: isMobile ? { textAlign: 'center' } : undefined,
        };
    }
  };

export const getDefaultColumnHeader = (labelTeam: string) => {
  return { value: labelTeam, css: { flexGrow: '1' } };
};

const generateFootballStandingsColumnsHeaderByDeviceType =
  (dataElements: Array<string>, labels: Record<string, string>) => (isMobile: boolean, tooltips?: Record<string, string>) => {
    const dynamicColumnsHeaderGenerator = createColumnHeader(isMobile);
    const defaultColumnHeader = getDefaultColumnHeader(labels.labelTeam);

    const dynamicColumnsHeader = dataElements.map((dataElement) => {
      const label = getLabelByDataElement(dataElement, labels);
      const tooltip = getTooltipByDataElement(dataElement, tooltips);

      return dynamicColumnsHeaderGenerator(dataElement, label, tooltip);
    });

    return [defaultColumnHeader, ...dynamicColumnsHeader];
  };

export const generateFootballStandingsColumnsHeader = (dataLabels: IStandingsConfig['dataLabels'], dataElements: Array<string>) => {
  const { labels, tooltips } = generateFootballStandingsColumnsLabels(dataLabels);
  const columnsGenerator = generateFootballStandingsColumnsHeaderByDeviceType(dataElements, labels);

  return {
    mobileColumns: columnsGenerator(true),
    desktopColumns: columnsGenerator(false, tooltips),
  };
};
// Standings columns header related end

// Standings columns values related
type LinkCreationServiceArg = LinkCreationService | null | undefined;

export type TeamFormAndEntityLinks = { dataEntityLinks?: IFootballConfig['dataEntityLinks']; teamForm?: IForm[] };

type ColumnCoreValue = string | INormalizedTeamForm[];

export const extractFootballStandingsValues = (
  standing: IStanding,
  isMobile: boolean,
  dataTeamNameType?: IFootballConfig['dataTeamNameType'],
) => {
  const goalDiffSign = standing?.goalsFor > standing?.goalsAgainst ? '+' : '';
  const goalDiffNumber = standing?.goalsFor - standing?.goalsAgainst;
  const goalDiff = `${goalDiffSign}${goalDiffNumber}`;
  const goalDifferenceResult = isMobile ? goalDiff : `${standing?.goalsFor} : ${standing?.goalsAgainst} (${goalDiff})`;

  const teamId = String(standing?.team?.id);
  const teamName = getTeamName(standing?.team, dataTeamNameType) ?? '';
  const teamSlug: string = standing?.team?.slug ?? '';
  const teamType = standing?.team?.type ?? '';

  // If value is undefined, null or empty string, return '-'
  const rank = getLabelValue(standing?.rank, '-');
  const teamLogo = getLabelValue(standing?.team?.logo, assetsPlaceholder.team);
  const matchesPlayed = getLabelValue(standing?.played, '-');
  const points = getLabelValue(standing?.points, '-');
  const wins = getLabelValue(standing?.wins, '-');
  const draws = getLabelValue(standing?.draws, '-');
  const defeats = getLabelValue(standing?.defeits, '-');

  return {
    teamId,
    teamName,
    teamSlug,
    rank,
    teamLogo,
    teamType,
    matchesPlayed,
    goalsDiff: goalDifferenceResult,
    points,
    wins,
    draws,
    defeats,
  };
};

export const extractColumnValueByDataElement = (
  dataElement: string,
  values: Record<string, string>,
  teamFormRelatedData?: TeamFormAndEntityLinks,
): ColumnCoreValue => {
  switch (dataElement) {
    case FootballStandingsElements.MATCHES_WON:
      return values.wins;
    case FootballStandingsElements.MATCH_DRAW:
      return values.draws;
    case FootballStandingsElements.MATCH_LOSSES:
      return values.defeats;
    case FootballStandingsElements.MATCHES_PLAYED:
      return values.matchesPlayed;
    case FootballStandingsElements.GOAL_DIFFERENCE:
      return values.goalsDiff;
    case FootballStandingsElements.POINTS:
      return values.points;
    case FootballStandingsElements.TEAM_FORM:
      return (
        teamFormRelatedData?.teamForm?.reduce<INormalizedTeamForm[]>((output, match) => {
          const normalizedForm = normalizeTeamFormData({
            match,
            dataEntityLinks: teamFormRelatedData?.dataEntityLinks,
          });
          if (normalizedForm) output.push(normalizedForm);
          return output;
        }, []) ?? []
      );
    default:
      return '-';
  }
};

export const createReusedColumn = (dataElement: string, value: ColumnCoreValue, urlTarget: string) => {
  switch (dataElement) {
    case FootballStandingsElements.MATCHES_WON:
    case FootballStandingsElements.MATCH_DRAW:
    case FootballStandingsElements.MATCH_LOSSES:
    case FootballStandingsElements.MATCHES_PLAYED:
      return {
        value,
        type: 'text',
        css: { width: '$6', textAlign: 'center' },
      };
    case FootballStandingsElements.TEAM_FORM:
      return {
        value,
        type: 'form',
        urlTarget,
      };
    default:
      return {};
  }
};

export const createNotDynamicColumns = (
  values: Record<string, string>,
  urlTarget: string,
  linkService: LinkCreationServiceArg,
  displayImage: boolean,
) => {
  const { teamId, teamName, rank, teamLogo, teamSlug, teamType } = values;

  return [
    { value: rank, type: 'text', css: { mr: '$3', mx: '12px', maxWidth: '$3', width: '100%', textAlign: 'center' } },
    {
      value: teamLogo,
      type: 'image',
      alt: teamName,
      css: { ...setNationalParticipantBorder(teamType === NATIONAL) },
    },
    {
      value: teamName ?? NOT_AVAILABLE,
      eventValues: { id: teamId, name: teamName },
      link: linkService?.createEntityUrl(String(teamId), TEAM_ENTITY, '', teamSlug) ?? '#',
      urlTarget,
      type: 'link',
      css: { flexGrow: '1', ml: '$2' },
    },
  ];
};

export const createDynamicColumnForMobile = (dataElement: string, value: ColumnCoreValue, urlTarget: string) => {
  if (dataElement === FootballStandingsElements.TEAM_FORM) {
    return {
      value,
      type: 'form',
      urlTarget,
    };
  }

  return {
    value,
    type: 'text',
    css: { textAlign: 'center' } as CSS,
  };
};

export const createDynamicColumnForDesktop = (dataElement: string, value: ColumnCoreValue, urlTarget: string) => {
  switch (dataElement) {
    case FootballStandingsElements.GOAL_DIFFERENCE:
      return {
        value,
        type: 'text',
        css: { width: '$9', textAlign: 'center' },
      };
    case FootballStandingsElements.POINTS:
      return {
        value,
        type: 'text',
        css: { width: '$6', textAlign: 'center' },
      };
    default:
      return createReusedColumn(dataElement, value, urlTarget);
  }
};

const createDynamicColumns = (isMobile: boolean, urlTarget: string) => (dataElement: string, value: ColumnCoreValue) => {
  return isMobile
    ? createDynamicColumnForMobile(dataElement, value, urlTarget)
    : createDynamicColumnForDesktop(dataElement, value, urlTarget);
};

export const columnsValuesGenerator =
  (
    dataElements: Array<string>,
    values: Record<string, string>,
    linkService: LinkCreationServiceArg,
    teamFormRelatedData: TeamFormAndEntityLinks,
    urlTarget: string = TARGET_BLANK,
    displayImage: boolean,
  ) =>
  (isMobile: boolean) => {
    const notDynamicColumns = createNotDynamicColumns(values, urlTarget, linkService, displayImage);
    const dynamicColumnsGenerator = createDynamicColumns(isMobile, urlTarget);

    const dynamicColumns = dataElements.map((dataElement) => {
      const extractedColumnValueByDataElement = extractColumnValueByDataElement(dataElement, values, teamFormRelatedData);
      return dynamicColumnsGenerator(dataElement, extractedColumnValueByDataElement);
    });

    return [...notDynamicColumns, ...dynamicColumns] as INormalizedStandingColumn[];
  };
// Standings columns values related end
